import { Badge, Button, Card, CardContent } from '@bs/ui-components'
import { ArrowUpRight, Building2, CalendarDays, FileClock } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { Activity } from './types'
import { getApplications, type Application } from '@/features/jobs/api'

type ActivityFilter = 'all' | 'application' | 'interview' | 'update'

// Legacy's `getStatusByEnum` labels, one per stage the candidate was shown.
const STATUS_LABEL: Record<Application['status'], { text: string; tone: Activity['tone'] }> = {
  submitted: { text: 'Application sent', tone: 'success' },
  viewed: { text: 'Viewed by recruiter', tone: 'default' },
  in_process: { text: 'In progress', tone: 'default' },
  interview: { text: 'Interview', tone: 'success' },
  offered: { text: 'Offered', tone: 'success' },
  hired: { text: 'Hired', tone: 'success' },
  on_hold: { text: 'On hold', tone: 'muted' },
  rejected: { text: 'Not selected', tone: 'warning' },
  withdrawn: { text: 'Withdrawn', tone: 'muted' },
}

/**
 * Real applications become activities directly — `bs-talent.applications` is the backing, and
 * since the process.events consumer writes the stage back, an application at `interview` IS the
 * interview activity (the 'interview' filter used to be empty by design: the stage lived only in
 * `bs-core.processes`). 'update' still has no backing — real mode has zero of those, never
 * fabricated ones.
 */
function toActivity(application: Application): Activity {
  const status = STATUS_LABEL[application.status]
  return {
    id: application._id,
    title: application.jobData?.title ?? '',
    company: application.companyData?.name ?? '',
    status: status.text,
    date: new Date(application.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
    tone: status.tone,
    category: application.status === 'interview' ? 'interview' : 'application',
    // `jobData.slug` is the actual job slug, snapshotted at apply time — absent on rows applied
    // before that field existed, in which case the "Open job" button has nothing to link to and
    // stays hidden (same "no slug, no link" rule the jobs page uses).
    ...(application.jobData?.slug ? { jobSlug: application.jobData.slug } : {}),
  }
}

export function ActivitiesPage() {
  const navigate = useNavigate()
  const [filter, setFilter] = useState<ActivityFilter>('all')
  const [selectedId, setSelectedId] = useState('')
  const [realActivities, setRealActivities] = useState<Activity[]>([])
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    let cancelled = false
    getApplications()
      .then(({ items }) => {
        if (cancelled) return
        setRealActivities(items.map(toActivity))
      })
      .catch(() => {
        // Surfaced, not swallowed — a failed fetch rendering the "no activities" empty state is
        // indistinguishable from a candidate who truly has none.
        if (!cancelled) setLoadError('Could not load your activities — refresh the page to try again.')
      })
    return () => {
      cancelled = true
    }
  }, [])

  const activities = realActivities
  const visibleActivities = filter === 'all' ? activities : activities.filter((activity) => activity.category === filter)
  const selectedActivity = activities.find((activity) => activity.id === selectedId) ?? visibleActivities[0]

  return (
    <main className="mx-auto max-w-[1460px] px-4 py-8 sm:px-6 md:py-12 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight text-primary sm:text-4xl">My activities</h1>
        <p className="mt-2 text-muted-foreground">Follow every opportunity from application to decision.</p>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <section className="min-w-0 flex-1">
          <div className="mb-5 flex gap-2 overflow-x-auto">
            <Button size="sm" variant={filter === 'all' ? 'default' : 'outline'} onClick={() => setFilter('all')}>All activity</Button>
            <Button size="sm" variant={filter === 'application' ? 'default' : 'outline'} onClick={() => setFilter('application')}>Applications</Button>
            <Button size="sm" variant={filter === 'interview' ? 'default' : 'outline'} onClick={() => setFilter('interview')}>Interviews</Button>
            <Button size="sm" variant={filter === 'update' ? 'default' : 'outline'} onClick={() => setFilter('update')}>Updates</Button>
          </div>

          {loadError ? <p role="alert" className="mb-4 rounded-xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">{loadError}</p> : null}
          <Card className="overflow-hidden">
            <CardContent className="divide-y divide-border p-0">
              {visibleActivities.map((activity) => (
                <article key={activity.id} className={`flex flex-col gap-4 p-5 transition hover:bg-[#fbfbfc] sm:flex-row sm:items-center sm:p-6 ${selectedActivity?.id === activity.id ? 'bg-accent/[0.04]' : ''}`}>
                  <div className="flex size-14 shrink-0 items-center justify-center rounded-full bg-[#f1f4ff] text-primary">
                    <Building2 className="size-6" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="font-semibold text-[#404040]">{activity.title}</h2>
                    <p className="mt-1 text-sm font-medium text-primary">{activity.company}</p>
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground"><CalendarDays className="size-3.5" /> {activity.date}</p>
                  </div>
                  <Badge variant={activity.tone}>{activity.status}</Badge>
                  <Button variant="ghost" size="sm" aria-label="View activity details" onClick={() => setSelectedId(activity.id)}>Details</Button>
                </article>
              ))}
            </CardContent>
          </Card>
          {visibleActivities.length === 0 && !loadError ? <div className="mt-4 rounded-xl border border-dashed border-[#c7c7c7] bg-white p-10 text-center text-sm text-muted-foreground">No activities in this category yet.</div> : null}
        </section>
        <aside className="lg:w-[360px] lg:shrink-0">{selectedActivity ? <Card className="lg:sticky lg:top-5"><CardContent className="p-6"><div className="flex size-12 items-center justify-center rounded-xl bg-[#f1f4ff] text-primary"><FileClock className="size-6" /></div><p className="mt-5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Activity details</p><h2 className="mt-2 text-xl font-semibold text-primary">{selectedActivity.title}</h2><p className="mt-1 font-medium text-[#555]">{selectedActivity.company}</p><div className="mt-5 flex items-center justify-between gap-3"><Badge variant={selectedActivity.tone}>{selectedActivity.status}</Badge><span className="text-xs text-muted-foreground">{selectedActivity.date}</span></div><p className="mt-5 leading-6 text-[#555]">{selectedActivity.details ?? 'No additional details for this update yet.'}</p>{selectedActivity.jobSlug ? <Button className="mt-6 w-full" onClick={() => navigate(`/job/${selectedActivity.jobSlug}`)}>Open job <ArrowUpRight className="size-4" /></Button> : null}</CardContent></Card> : null}</aside>
      </div>
    </main>
  )
}
